import { useState, type ReactNode } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { Action, CodeBlock } from "./ui";
import { deploymentUrl } from "../../lib/deployment-url";

const docLinks = [
  {
    href: "/docs",
    label: "Getting started",
    description:
      "Installation, first project, connecting agents, and troubleshooting",
    keywords: "install npm node setup help error connection offline port",
  },
  {
    href: "/docs/workspace",
    label: "Canvas guide",
    description:
      "Pages, comments, invitations, keyboard shortcuts, and exports",
    keywords:
      "keyboard shortcut undo redo zoom pan layer text image SVG JSON export",
  },
  {
    href: "/docs/mcp",
    label: "MCP connection",
    description:
      "Configure agent access and explore the available tools",
    keywords: "mcp stdio http agent tools resource configuration key token",
  },
  {
    href: "/docs/api",
    label: "REST API",
    description: "Authentication, projects, elements, batch edits, and errors",
    keywords:
      "rest api curl json openapi revision 409 conflict batch authentication delete",
  },
];

export function DocsPage({ path }: { path: string }) {
  const [query, setQuery] = useState("");
  const results = docLinks.filter((entry) =>
    (entry.label + " " + entry.description + " " + entry.keywords)
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  const title = docLinks.find((entry) => entry.href === path)!.label;
  return (
    <div className="mk-docs mk-container">
      <aside className="mk-docs-sidebar">
        <span className="mk-eyebrow">The field guide</span>
        <label className="mk-search">
          <Search size={16} />
          <input
            type="search"
            aria-label="Search documentation"
            placeholder="Find a guide…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {query.trim() ? (
          <div className="mk-search-results">
            <span role="status">
              {results.length} {results.length === 1 ? "guide" : "guides"} found
            </span>
            {results.map((entry) => (
              <a key={entry.href} href={entry.href}>
                <strong>{entry.label}</strong>
                <span>{entry.description}</span>
              </a>
            ))}
            {!results.length && (
              <p>Try “MCP”, “export”, “keyboard”, or “install”.</p>
            )}
          </div>
        ) : (
          <nav aria-label="Documentation">
            {docLinks.map((entry) => (
              <a
                key={entry.href}
                href={entry.href}
                aria-current={path === entry.href ? "page" : undefined}
              >
                {entry.label}
              </a>
            ))}
          </nav>
        )}
        <div className="mk-docs-help">
          <p>Ready to make something?</p>
          <a className="mk-text-link" href="/app">
            Open your workspace <ArrowUpRight size={14} />
          </a>
        </div>
      </aside>
      <article className="mk-prose mk-docs-article">
        <span className="mk-eyebrow">Documentation / v0.1</span>
        <h1>{title}</h1>
        {path === "/docs" ? (
          <GettingStarted />
        ) : path === "/docs/workspace" ? (
          <WorkspaceGuide />
        ) : path === "/docs/mcp" ? (
          <McpGuide />
        ) : (
          <ApiGuide />
        )}
        <div className="mk-docs-next">
          <span>Keep exploring</span>
          {docLinks
            .filter((entry) => entry.href !== path)
            .map((entry) => (
              <a href={entry.href} key={entry.href}>
                {entry.label}
                <ArrowUpRight size={15} />
              </a>
            ))}
        </div>
      </article>
    </div>
  );
}

function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id}>
      <h2>
        <a href={"#" + id}>
          {title}
          <span className="mk-anchor" aria-hidden="true">
            #
          </span>
        </a>
      </h2>
      {children}
    </section>
  );
}

function GettingStarted() {
  return (
    <>
      <p className="mk-lead">
        From an empty canvas to your first agent edit. Start locally, then make
        the workspace your own.
      </p>
      <div className="mk-callout">
        Already running the app? <a href="/app">Open the workspace</a> and skip
        to <a href="#first-design">your first design</a>.
      </div>
      <DocSection id="installation" title="1. Run the workspace">
        <p>
          In the repository, install dependencies and start the app. You need
          Node.js 22.13 or newer with the built-in SQLite runtime.
        </p>
        <CodeBlock
          label="Terminal · from the repository"
          value={"npm ci\nnpm run dev"}
        />
        <p>
          Open <a href="http://127.0.0.1:5173/app">127.0.0.1:5173/app</a>. The
          API and MCP server run on port 4310 by default. Keep both processes
          running while you work.
        </p>
        <details className="mk-doc-detail">
          <summary>Run the built app from one server</summary>
          <CodeBlock
            label="Terminal · production build"
            value={"npm run build\nnpm start"}
          />
          <p>
            Open <a href="http://127.0.0.1:4310/app">127.0.0.1:4310/app</a>. The
            marketing pages, editor, API, and MCP endpoint are served by the
            same local process.
          </p>
        </details>
      </DocSection>
      <DocSection id="first-design" title="2. Create your account and canvas">
        <ol>
          <li>Open the app and choose Create an account.</li>
          <li>
            Enter your name and email, then request a sign-in code.
          </li>
          <li>
            Enter the eight-digit code sent to your email. It expires after ten
            minutes and works once.
          </li>
          <li>Choose New project. Start blank or with an editable sample.</li>
        </ol>
        <p>
          Your account’s canvases are private. The existing local workspace
          remains separate and is available only on a local installation.
        </p>
        <Action>Create your account</Action>
      </DocSection>
      <DocSection
        id="first-agent"
        title="3. Bring your agent into the conversation"
      >
        <p>
          Open a canvas and choose <strong>Connect agent</strong>, then
          <strong> Copy setup instructions</strong>. Paste them into your agent.
          It opens the guide, chooses its own name, and connects to your canvas.
          The app confirms when it makes its first request. Access defaults to
          this canvas, with design and comment permission for 30 days.
        </p>
        <CodeBlock
          label="A first prompt for your connected agent"
          value="Read my canvas and its comments. Create a homepage for my product, using the latest project revision. Reply to feedback and mark it handled after addressing it."
        />
        <p>
          See the <a href="/docs/mcp">MCP guide</a> or the{" "}
          <a href="/docs/api">API guide</a>. Once a design appears, choose
          Comment and click the design to leave feedback.
        </p>
      </DocSection>
      <DocSection id="help" title="Help & troubleshooting">
        <dl>
          <dt>The workspace cannot connect</dt>
          <dd>
            Check that the app’s server is running. Open{" "}
            <a href="/api/health">the health endpoint</a>; it should return an
            “ok” response. Restart the local development process if needed.
          </dd>
          <dt>A port is already in use</dt>
          <dd>
            Stop the duplicate app process. To change the API port, set the{" "}
            <code>PORT</code> environment variable before starting the app; use
            a new setup link from Connect agent.
          </dd>
          <dt>An agent receives an authentication error</dt>
          <dd>
            If a setup link expired or was already used, create a new link in
            Connect agent. For an existing connection, check whether its key
            expired or was revoked. Local URLs require an agent on the same
            computer. Manual stdio paths must match your installation.
          </dd>
          <dt>An edit returns a revision conflict</dt>
          <dd>
            Read the project again and retry the intended change using its
            latest revision. Another edit arrived before your request.
          </dd>
          <dt>An external image does not load</dt>
          <dd>
            Use a direct HTTPS image URL that the browser can access. Some hosts
            block external embedding or require authentication.
          </dd>
        </dl>
        <p>
          For reproducible issues, keep the steps, app version, and error
          message with your repository’s development notes. Remove agent keys
          before sharing logs.
        </p>
      </DocSection>
      <DocSection id="storage" title="Your local data">
        <p>
          The app stores designs and hashed agent keys in{" "}
          <code>.data/workspace.sqlite</code>. Set{" "}
          <code>PROXIMAFORMA_DATA_DIR</code> to an absolute path to use a
          different directory. Stop the app before making a simple file-copy
          backup.
        </p>
        <p>
          Read <a href="/privacy">Your data</a> for agent access, browser
          storage, and external image behavior.
        </p>
      </DocSection>
    </>
  );
}

const shortcuts = [
  ["V / H", "View / pan"],
  ["C", "Leave a pinned comment"],
  ["Escape", "Cancel a draft comment"],
  ["⌘ / Ctrl + 0", "Fit the canvas"],
  ["+ / −", "Zoom around the last hovered canvas position"],
  ["⌘ / Ctrl + wheel", "Zoom around the pointer"],
];
function WorkspaceGuide() {
  return (
    <>
      <p className="mk-lead">
        The canvas puts your design first. Open the pages or comments panel when
        you need it, then close it to make room.
      </p>
      <DocSection id="elements" title="Find your pages">
        <p>
          Each project contains pages, and each page has its own canvas. Use the
          Pages panel to switch canvases, or add and rename pages. Existing work
          opens together on Page 1. Frames are artboards within a page: choose a
          frame to focus it, or choose View all frames. Agents can manage pages
          with edit_page and place root frames using pageId.
        </p>
      </DocSection>
      <DocSection id="editing" title="Leave a useful comment">
        <p>
          Choose Comment in the bottom palette, or press C. Click a location on
          the design and write your feedback. Colleagues and agents can reply to
          the same thread. @name mentions can be found with the mentions filter;
          this version does not send notifications.
        </p>
      </DocSection>
      <DocSection id="comments" title="Track what has been handled">
        <p>
          Each thread starts as New. Move it to In progress or Handled, or
          choose Mark handled. A new reply to a handled thread moves it back to
          In progress. Reopen as New brings it back into review. The Filter
          button lets you choose statuses, page, author, and mentions.
          Search includes replies, and you can sort by recent activity or oldest
          first.
        </p>
      </DocSection>
      <DocSection id="sharing" title="Invite a reviewer">
        <p>
          Canvas owners can create a single-use invitation link with view-only
          or commenting access. Links expire after seven days and can be
          revoked. Reviewers register before accepting. You can remove their
          access from the same dialog.
        </p>
      </DocSection>
      <DocSection id="vectors" title="Editable artwork">
        <p>
          Designs contain real text, shapes, images, and vector paths. Agents
          use the same structured elements as the original editor. For manual
          editing, the advanced editor remains at{" "}
          <a href="/app?editor=1">the advanced workspace</a>. Reviewers cannot
          use it to bypass their permissions.
        </p>
      </DocSection>
      <DocSection id="appearance" title="Choose your appearance">
        <p>
          Choose System, Light, or Dark from the workspace dashboard. Artwork
          retains its authored colors regardless of the surrounding interface
          theme.
        </p>
      </DocSection>
      <DocSection id="shortcuts" title="Keyboard shortcuts">
        <div className="mk-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Shortcut</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {shortcuts.map(([key, label]) => (
                <tr key={key}>
                  <td>
                    <kbd>{key}</kbd>
                  </td>
                  <td>{label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocSection>
      <DocSection id="agent-edits" title="See changes as they happen">
        <p>
          Edits and comments arrive through a live connection, with periodic
          refresh as a fallback. Revision checks reject stale canvas writes and
          stale comment status changes. Agent attribution is available in
          comment threads and in the document activity record.
        </p>
      </DocSection>
      <DocSection id="exports" title="Take your design with you">
        <p>
          The download button in the palette exports SVG. The API exports JSON
          as well. Exports contain editable design data, not runnable
          application code. SVG text wrapping can differ from the browser, and
          external images remain linked.
        </p>
        <p>
          Choose Upload project in your workspace (or the manual editor’s project
          menu) to open a ProximaForma JSON export as a new project. Choose or drop
          one file up to 2 MB, then choose Upload and open.
          Artwork is preserved; comments, history, sharing settings, and reference-file
          links stay with the original project.
        </p>
      </DocSection>
    </>
  );
}

const mcpTools = [
  ["list_comments", "Read feedback and replies, filtered by page or status."],
  ["add_comment", "Pin feedback to a page or canvas location."],
  ["reply_to_comment", "Reply to a colleague or agent in the same thread."],
  [
    "update_comment_status",
    "Mark feedback new, in_progress, or handled with its comment revision.",
  ],
  [
    "create_vector_path",
    "Create a custom path with anchors and optional cubic Bézier handles.",
  ],
  [
    "create_vector_shape",
    "Create an editable line, triangle, polygon, or star.",
  ],
  [
    "edit_vector_point",
    "Move, reshape, split, or delete a zero-based path anchor.",
  ],
  ["list_projects", "List projects and their current revisions."],
  ["get_project", "Read a project’s complete element tree and activity. Pass optional elementIds to inspect specific elements and show their names in the agent activity panel."],
  ["create_project", "Create an owned blank canvas or sample design. The connection can read and edit canvases it creates."],
  ["create_invitation", "Create a single-use viewer or commenter invitation link, valid for seven days. No email is sent."],
  ["generate_image", "Generate an image with OpenAI using Forma credits, then place its saved src on the canvas. Hosted requests return a background status; repeat identical arguments to check progress."],
  ["list_generated_images", "Find saved image assets and recover completed generations after an interruption."],
  ["add_element", "Add an element, optionally inside a frame."],
  [
    "update_element",
    "Change an element’s position, appearance, text, or parent.",
  ],
  ["delete_element", "Delete an element and its descendants."],
  ["batch_edit", "Apply up to 200 operations as one atomic batch."],
  ["reorder_element", "Change an element’s index in the flat element array."],
  ["rename_project", "Give a project a new name."],
  ["export_svg", "Export the current design as SVG."],
];

function McpGuide() {
  return (
    <>
      <p className="mk-lead">
        Let your agent read and edit real design elements. The app supports
        local stdio and Streamable HTTP connections.
      </p>
      <DocSection id="stdio" title="Connect an MCP client">
        <ol>
          <li>Keep the Proximaforma app running.</li>
          <li>
            Open <strong>Connect agent</strong> in the workspace.
          </li>
          <li>
            Choose <strong>Copy setup instructions</strong> and paste them into
            your agent. Optional Access settings controls scope and expiry.
          </li>
          <li>
            The agent chooses its own name and registers. It reads your canvas
            and comments, then confirms it is ready for instructions.
          </li>
        </ol>
        <p>
          Registration gives the agent its credential and a Streamable HTTP
          configuration. It can use REST immediately, or add the MCP server if
          its client supports that. Otherwise, add the returned configuration in
          your client’s settings. For ChatGPT, use the account-linking flow below.
        </p>
        <p>
          Setup links are private, valid for 24 hours, and work once. Fetching
          the guide does not use the link. If registration succeeds but its
          response is lost, revoke that connection and create a new link. Local
          setup links work only for agents on this computer.
        </p>
      </DocSection>
      <DocSection id="chatgpt" title="Connect ChatGPT">
        <p><a href="/connect/chatgpt">Add to ChatGPT</a> for guided desktop and browser setup, connection status, and mobile availability.</p>
        <p>In the desktop connection form, choose <strong>Streamable HTTP</strong> and enter <code>{deploymentUrl("/mcp")}</code>. Save, then sign in. No command, client secret, or manual callback configuration is needed.</p>
        <p>
          Revoke access from that canvas’s Connect agent panel. This also revokes
          token refresh. Account recovery revokes all existing connections.
          Linking does not allow creating other canvases or managing sharing.
        </p>
        <p>
          Public directory installation requires OpenAI review and publication;
          it is not available merely because the server supports OAuth. Availability
          in ChatGPT, including on phones, depends on your account and workspace.
          See <a href="https://developers.openai.com/plugins/deploy/connect-chatgpt">OpenAI’s connection guide</a>.
        </p>
      </DocSection>
      <DocSection id="http" title="Connect over Streamable HTTP">
        <p>
          The setup guide tells the agent how to exchange its one-time link and
          chosen name for a scoped key. Registration returns the complete MCP
          configuration. For manual client configuration, use the values below.
          The endpoint uses stateless JSON responses.
        </p>
        <CodeBlock
          label="Default HTTP connection values"
          value={
            "Endpoint: http://127.0.0.1:4310/mcp\nTransport: Streamable HTTP\nAuthorization: Bearer YOUR_API_KEY"
          }
        />
        <p>
          If you changed the server port, use the endpoint shown in the app. The
          default server listens only on this computer; remote clients cannot
          reach it without a separate hosting and authentication setup.
        </p>
      </DocSection>
      <DocSection id="tools" title="Available tools">
        <div className="mk-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Tool</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {mcpTools.map(([name, description]) => (
                <tr key={name}>
                  <td>
                    <code>{name}</code>
                  </td>
                  <td>{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocSection>
      <DocSection id="resources" title="Resources and editing guidance">
        <p>
          The server exposes <code>proximaforma://guide</code> and{" "}
          <code>proximaforma://projects/&#123;projectId&#125;</code>. Connected
          agents also receive design instructions from the server.
        </p>
        <ul>
          <li>Read the project before editing and pass its latest revision.</li>
          <li>
            Use <code>batch_edit</code> with explicit element IDs to add frames
            and children together.
          </li>
          <li>
            Keep parent IDs valid. Only frames can contain other elements.
          </li>
          <li>Inspect the result after a change and refine as needed.</li>
        </ul>
      </DocSection>
      <DocSection id="access" title="Manage agent access">
        <p>
          Each named key is restricted to its owner’s accessible canvases,
          optionally one initial canvas, and to read, comment, or design access.
          Editing agents can create canvases and invite viewers or commenters.
          A canvas-scoped connection can access its initial canvas and canvases
          it creates; unrelated canvases stay private. Keys expire in 7–90 days
          and can be revoked. Agents cannot administer connections or remove members.
        </p>
        <p>
          The stdio bridge uses the local workspace credential by default. For a
          named agent identity, set <code>PROXIMAFORMA_API_KEY</code> in its
          environment. Read <a href="/privacy">Your data</a> before connecting a
          provider.
        </p>
      </DocSection>
    </>
  );
}

const createRequest =
  'curl http://127.0.0.1:4310/api/projects \\\n  -H "Authorization: Bearer $PROXIMAFORMA_API_KEY" \\\n  -H \'Content-Type: application/json\' \\\n  -d \'{"name":"My application","template":"blank"}\'';
const batchRequest =
  'curl http://127.0.0.1:4310/api/projects/PROJECT_ID/operations \\\n  -H "Authorization: Bearer $PROXIMAFORMA_API_KEY" \\\n  -H \'Content-Type: application/json\' \\\n  -d \'{\n    "revision": 0,\n    "operations": [\n      {"op":"add","element":{\n        "id":"home","type":"frame","name":"Home",\n        "width":1200,"height":800,"fill":"#ffffff"\n      }},\n      {"op":"add","element":{\n        "id":"headline","type":"text","parentId":"home",\n        "name":"Headline","text":"Your next idea",\n        "x":64,"y":80,"width":650,"height":100,\n        "fontSize":56,"fontWeight":700\n      }}\n    ]\n  }\'';
const endpoints = [
  [
    "PATCH /projects/:id/elements/:elementId/nodes/:index",
    "Edit an individual vector anchor with an edit action and the current revision.",
  ],
  ["GET /projects", "List projects."],
  ["POST /projects/import", "Upload a JSON export as a new project; up to 2 MB."],
  [
    "POST /projects",
    "Create a project with name and optional blank or starter template.",
  ],
  ["GET /projects/:id", "Read a complete project."],
  ["PATCH /projects/:id", "Rename with name and the current revision."],
  [
    "DELETE /projects/:id",
    "Delete a project; revision is required in the JSON body.",
  ],
  ["GET /projects/:id/elements", "List the project’s elements."],
  [
    "POST /projects/:id/elements",
    "Add an element; send element and the current revision.",
  ],
  ["GET /projects/:id/elements/:elementId", "Read one element."],
  [
    "PATCH /projects/:id/elements/:elementId",
    "Update with changes and the current revision.",
  ],
  [
    "DELETE /projects/:id/elements/:elementId",
    "Delete an element and descendants; the current revision in the body.",
  ],
  [
    "POST /projects/:id/operations",
    "Apply add, update, delete, and reorder operations atomically.",
  ],
  [
    "POST /projects/:id/restore",
    "Replace the element tree with elements and a required revision.",
  ],
  [
    "GET /projects/:id/export?format=svg",
    "Export SVG; use format=json for a structured document.",
  ],
  ["GET /events", "Subscribe to design changes and agent-activity events with server-sent events."],
  ["GET /projects/:id/agent-activity", "Read the latest 100 agent reads, edits, comments, and exports, including the affected element names."],
];

function ApiGuide() {
  return (
    <>
      <p className="mk-lead">
        A direct way to put ideas on the canvas. Use JSON over HTTP to read
        projects and edit elements.
      </p>
      <a
        className="mk-text-link"
        href="/openapi.json"
        download="proximaforma-openapi.json"
      >
        Download the OpenAPI reference <ArrowUpRight size={16} />
      </a>
      <DocSection
        id="accounts-comments"
        title="Accounts, comments, and invitations"
      >
        <p>
          Registration, sign-in, and recovery use the browser session endpoints.
          Canvas comments have their own revisions. Read threads at{" "}
          <code>GET /api/projects/:id/comments</code>, post a thread to the same
          path, reply at{" "}
          <code>POST /api/projects/:id/comments/:commentId/replies</code>, and
          update status with{" "}
          <code>PATCH /api/projects/:id/comments/:commentId</code>. The complete
          schemas are in <a href="/openapi.json">OpenAPI</a>.
        </p>
      </DocSection>
      <DocSection id="authentication" title="Authentication">
        <p>
          In the workspace, open Connect agent and copy the setup instructions
          to your agent. It registers through{" "}
          <code>POST /api/agent-setups/claim</code>
          using the setup token and its chosen name. Store the returned key as{" "}
          <code>PROXIMAFORMA_API_KEY</code> in your private environment, then
          send it in the <code>Authorization</code> header. The default base URL
          is <code>http://127.0.0.1:4310/api</code>.
        </p>
        <p>
          Keys follow their configured canvas scope, permission, expiry, and
          account membership. Creating setup links and directly managing keys
          requires the owner’s browser session. Claiming a link needs only its
          one-time token; the agent cannot expand its access. Never embed a key
          in public frontend code.
        </p>
      </DocSection>
      <DocSection id="create-project" title="1. Create a project">
        <CodeBlock
          label="Shell · create a blank project"
          value={createRequest}
        />
        <p>
          The response includes the project’s <code>id</code> and current{" "}
          <code>revision</code>. Replace <code>PROJECT_ID</code> in the next
          example with that returned ID.
        </p>
      </DocSection>
      <DocSection id="batch" title="2. Add a frame and its headline">
        <CodeBlock
          label="Shell · add elements in one batch"
          value={batchRequest}
        />
        <p>
          Use the project’s current revision; the example uses <code>0</code>{" "}
          for a newly created blank project. The API returns the updated
          project.
        </p>
      </DocSection>
      <DocSection id="element-model" title="The element model">
        <p>
          Supported types: <code>frame</code>, <code>text</code>,{" "}
          <code>rectangle</code>, <code>ellipse</code>, <code>button</code>,{" "}
          <code>image</code>, and <code>path</code>. Only <code>type</code> is
          required when adding an element; other properties have defaults.
        </p>
        <ul>
          <li>
            <code>parentId</code>: a containing frame’s ID, or <code>null</code>{" "}
            for the canvas.
          </li>
          <li>
            <code>x</code>, <code>y</code>, <code>width</code>,{" "}
            <code>height</code>: position relative to the parent and dimensions
            in design pixels.
          </li>
          <li>
            <code>fill</code>, <code>color</code>, <code>stroke</code>:
            hexadecimal colors. Fill and stroke also accept{" "}
            <code>transparent</code>.
          </li>
          <li>
            <code>text</code>, <code>fontSize</code>, <code>fontWeight</code>,{" "}
            <code>textAlign</code>: text content and typography.
          </li>
          <li>
            <code>radius</code>, <code>opacity</code>, <code>rotation</code>:
            appearance and rotation in degrees.
          </li>
          <li>
            <code>blur</code>: Gaussian layer blur from 0 to 200 design pixels.
            Blurs the element and its contents, including children of a frame.
            Defaults to <code>0</code>; set it to <code>0</code> to remove blur.
          </li>
          <li>
            <code>src</code>: an HTTPS image URL or a saved path returned by{" "}
            <code>generate_image</code>. Text is plain text, not
            executable HTML.
          </li>
        </ul>
      </DocSection>
      <DocSection id="vector-paths" title="Vector paths and points">
        <p>
          Create a <code>path</code> through the standard element endpoint. Its{" "}
          <code>vector</code> field contains local <code>width</code>,{" "}
          <code>height</code>, <code>closed</code>, and an array of{" "}
          <code>nodes</code>. Each node has <code>x</code> and <code>y</code>,
          with optional <code>in</code> and <code>out</code> control points in
          the same coordinate system. Element width and height scale that
          geometry.
        </p>
        <CodeBlock
          label="JSON · an editable curve"
          value={JSON.stringify(
            {
              element: {
                type: "path",
                name: "Curve",
                x: 40,
                y: 80,
                width: 200,
                height: 100,
                fill: "transparent",
                stroke: "#303030",
                strokeWidth: 3,
                vector: {
                  width: 200,
                  height: 100,
                  closed: false,
                  nodes: [
                    { x: 0, y: 100, out: { x: 60, y: 0 } },
                    { x: 200, y: 100, in: { x: 140, y: 0 } },
                  ],
                },
              },
            },
            null,
            2,
          )}
        />
        <p>
          Use{" "}
          <code>PATCH /api/projects/:id/elements/:elementId/nodes/:index</code>{" "}
          for an individual point, with a zero-based index and a body such as{" "}
          <code>{'{"edit":{"action":"smooth"},"revision":1}'}</code>. Actions
          are move, handles, smooth, corner, split, and delete. Move requires a
          position; handles accepts in/out coordinates or null to remove a
          handle. A split adds a midpoint on the outgoing segment while
          preserving its curve.
        </p>
        <p>
          Paths require 2–256 anchors; closed paths require at least three.
          Coordinates and handles must be finite and within −20,000 to 20,000.
          The point endpoint uses the read revision when one is omitted. Agents
          can also use create_vector_path, create_vector_shape, and
          edit_vector_point over MCP.
        </p>
      </DocSection>
      <DocSection id="endpoints" title="Endpoint reference">
        <p>
          All paths below are relative to <code>/api</code>.
        </p>
        <div className="mk-table-scroll">
          <table className="mk-endpoints">
            <thead>
              <tr>
                <th>Method & path</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map(([method, purpose]) => (
                <tr key={method}>
                  <td>
                    <code>{method}</code>
                  </td>
                  <td>{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocSection>
      <DocSection id="revisions" title="Revisions, errors, and limits">
        <p>
          Always send the latest <code>revision</code> with edits. A stale
          request returns <strong>409</strong>. Read the project again and
          reapply the intended change. Invalid batches do not partially modify a
          design.
        </p>
        <div className="mk-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["400", "Invalid data, element tree, or operation."],
                ["401", "Missing, invalid, or revoked credential."],
                ["403", "Origin or action is not allowed."],
                ["404", "Project, element, or endpoint does not exist."],
                ["409", "Revision conflict; reload the project."],
                ["413", "Request exceeds the 2 MB body limit."],
              ].map(([status, meaning]) => (
                <tr key={status}>
                  <td>
                    <code>{status}</code>
                  </td>
                  <td>{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          No fixed element-count limit. Limits: 100 projects, 200 operations per
          batch, 100 agent keys, and 2 MB per request.
        </p>
      </DocSection>
    </>
  );
}
